/**
 * Signing in, when the API says it needs it -- and nothing at all when it does not.
 *
 * The browser does not decide whether FOREMAN requires authentication; the API
 * does, and says so at `GET /api/v1/config`, the one route it serves without a
 * token. A laptop with no Entra configured answers `authRequired: false` and the
 * UI behaves exactly as it always has: no MSAL instance, no redirect, no header.
 *
 * When it is required, sign-in is the redirect flow (a popup is blocked often
 * enough to be the wrong default), the token is acquired silently per request
 * for the API's own scope, and MSAL's cache does the remembering. The cache is
 * sessionStorage rather than localStorage: a token that outlives the tab is a
 * token that outlives the person's intent to be signed in on this machine.
 *
 * Everything here is module state on purpose. The API client is a plain module,
 * not a component, and it needs a token on every call -- threading one through
 * React context to reach `fetch` would put the auth layer in every component's
 * props for the sake of the one function that uses it.
 */

// Types only at module scope: MSAL itself is imported when, and only when, the
// API asks for sign-in, so a laptop build that never signs in never downloads
// it (it is most of a megabyte before minification).
import type { AccountInfo, PublicClientApplication } from "@azure/msal-browser";

export interface ClientConfig {
  authRequired: boolean;
  tenantId: string;
  clientId: string;
  scope: string;
  env: string;
  allowUserTools: boolean;
}

const OFF: ClientConfig = {
  authRequired: false,
  tenantId: "",
  clientId: "",
  scope: "",
  env: "",
  allowUserTools: true,
};

let config: ClientConfig = OFF;
let msal: PublicClientApplication | null = null;
let account: AccountInfo | null = null;

/** What the API told us at start-up. `authRequired: false` until then. */
export function clientConfig(): ClientConfig {
  return config;
}

/** The MSAL instance, for `MsalProvider`; null when auth is off. */
export function msalInstance(): PublicClientApplication | null {
  return msal;
}

/** Who the API will record as having done what you do; null when auth is off. */
export function signedInUser(): string | null {
  return account ? account.username || account.name || account.homeAccountId : null;
}

/**
 * Read the config and, if it asks for it, sign in.
 *
 * Resolves `"ready"` when the app can render, or `"redirecting"` when the page
 * is about to leave for Entra -- in which case rendering anything would only
 * flash before the navigation.
 */
export async function startAuth(): Promise<"ready" | "redirecting"> {
  const response = await fetch("/api/v1/config");
  if (!response.ok) {
    // An API from before /config existed. It cannot be requiring a token the
    // UI has no way to learn how to get, so this is the old behaviour.
    config = OFF;
    return "ready";
  }
  config = { ...OFF, ...((await response.json()) as Partial<ClientConfig>) };
  if (!config.authRequired) return "ready";

  if (!config.clientId || !config.tenantId || !config.scope) {
    throw new Error(
      "The API requires sign-in but did not say how: FOREMAN_ENTRA_SPA_CLIENT_ID, " +
        "FOREMAN_ENTRA_TENANT_ID and FOREMAN_ENTRA_AUDIENCE must all be set on it.",
    );
  }

  const { PublicClientApplication } = await import("@azure/msal-browser");
  msal = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: window.location.origin,
    },
    cache: { cacheLocation: "sessionStorage" },
  });
  await msal.initialize();

  // Coming back from Entra, this carries the account; otherwise a session
  // already in the cache does.
  const returned = await msal.handleRedirectPromise();
  account = returned?.account ?? msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  if (!account) {
    await msal.loginRedirect({ scopes: [config.scope] });
    return "redirecting";
  }
  msal.setActiveAccount(account);
  return "ready";
}

/**
 * `Authorization: Bearer …` for the API, or nothing when auth is off.
 *
 * Silent first, every time -- MSAL answers from its cache until the token is
 * near expiry and refreshes it behind the scenes. Only when Entra insists on
 * a person (consent, MFA, an expired session) does it fall back to the
 * redirect, and then this call never returns: the page is navigating away.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  if (!config.authRequired || !msal || !account) return {};
  try {
    const result = await msal.acquireTokenSilent({ scopes: [config.scope], account });
    return { Authorization: `Bearer ${result.accessToken}` };
  } catch (error) {
    // Already loaded by now: startAuth imported it to build `msal`.
    const { InteractionRequiredAuthError } = await import("@azure/msal-browser");
    if (error instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ scopes: [config.scope], account });
      return new Promise(() => undefined);
    }
    throw error;
  }
}

/**
 * Sign out: clears this tab's token cache and goes through Entra's logout
 * page, which ends the Entra session too -- the honest meaning of "sign out"
 * on a shared machine.
 */
export async function signOut(): Promise<void> {
  if (!msal) return;
  await msal.logoutRedirect({ account: account ?? undefined });
}
